import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { formatCalendarDate } from 'src/app/shared/utils/date.utils';

interface SprayRecordListItem {
  id: string;
  organizationId: string;
  organizationName: string;
  fieldName: string;
  cropType: string;
  customerName: string | null;
  applicationDate: string;
  status: string;
  totalAcreage: number;
  /**
   * Sum of flight counts this record contributes to a filing (FAA §F.2.d),
   * each aircraft floored to 1 as the report floors it. Null when the record
   * has no aircraft — renders "—" so a record that did not fly never
   * advertises a flight the filing does not contain.
   */
  filedFlights: number | null;
  /** At least one aircraft has no count and will be filed as 1. */
  needsFlightCount: boolean;
  pilotName: string;
  droneCount: number;
  droneNames: string[];
  chemicals: Array<{
    id: string;
    name: string;
    rate: string | null;
  }>;
}

interface Organization {
  id: string;
  name: string;
}

interface SprayRecordsResponse {
  success: boolean;
  data: {
    records: SprayRecordListItem[];
    total: number;
    page: number;
    pageSize: number;
  };
}

interface OrganizationsResponse {
  success: boolean;
  data: {
    organizations: Organization[];
    total: number;
  };
}

/**
 * Flight Records List Component
 * Browse spray records across all organizations with filters and pagination.
 * Read-only admin view -- no create, edit, or delete capabilities.
 */
@Component({
  selector: 'app-flight-records',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './flight-records.component.html',
  styleUrls: ['./flight-records.component.scss']
})
export class FlightRecordsComponent implements OnInit {
  records: SprayRecordListItem[] = [];
  organizations: Organization[] = [];
  loading = true;
  errorMessage = '';

  // Filters
  filterOrganizationId = '';
  filterStatus = '';
  filterStartDate = '';
  filterEndDate = '';

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;
  totalPages = 0;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadOrganizations();
    this.loadRecords();
  }

  async loadOrganizations(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<OrganizationsResponse>(
          `${environment.apiUrl}/admin/organizations?pageSize=500`
        )
      );

      if (response.success && response.data) {
        this.organizations = response.data.organizations;
      }
    } catch (error) {
      console.error('Failed to load organizations for filter:', error);
    }
  }

  async loadRecords(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const params = new URLSearchParams({
        page: this.currentPage.toString(),
        pageSize: this.pageSize.toString()
      });

      if (this.filterOrganizationId) {
        params.append('organizationId', this.filterOrganizationId);
      }
      if (this.filterStatus) {
        params.append('status', this.filterStatus);
      }
      if (this.filterStartDate) {
        params.append('startDate', this.filterStartDate);
      }
      if (this.filterEndDate) {
        params.append('endDate', this.filterEndDate);
      }

      const response = await firstValueFrom(
        this.http.get<SprayRecordsResponse>(
          `${environment.apiUrl}/admin/spray-records?${params.toString()}`
        )
      );

      if (response.success && response.data) {
        this.records = response.data.records;
        this.totalRecords = response.data.total;
        this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
      }
    } catch (error: any) {
      console.error('Failed to load flight records:', error);
      this.errorMessage = 'Failed to load flight records. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadRecords();
  }

  showClearFilters(): boolean {
    return !!(this.filterOrganizationId || this.filterStatus || this.filterStartDate || this.filterEndDate);
  }

  resetFilters(): void {
    this.filterOrganizationId = '';
    this.filterStatus = '';
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.onFilterChange();
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadRecords();
    }
  }

  viewRecord(record: SprayRecordListItem): void {
    this.router.navigate(['/flight-records', record.id]);
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'pending': return 'bg-warning text-dark';
      case 'cancelled': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  }

  /** Calendar-date safe — see shared/utils/date.utils.ts. */
  formatDate(date: string): string {
    return formatCalendarDate(date);
  }

  getFirstChemical(chemicals: SprayRecordListItem['chemicals']): string {
    if (!chemicals || chemicals.length === 0) return '-';
    const name = chemicals[0].name;
    if (chemicals.length > 1) {
      return `${name} (+${chemicals.length - 1})`;
    }
    return name;
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  get showingStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }
}
